<template>
  <v-dialog
    v-model="canShowDialog"
    max-width="674"
    :transition="false"
    persistent
    @keydown.enter="makeReservation"
    @keydown.esc="closeDialog(false)"
  >
    <v-card
      class="v-card"
      style="max-height: 70vh; display: flex; flex-direction: column;"
    >
      <v-card-title
        class="v-card-title"
      >
        {{ $t('fleet/vehicle_reservation.reservation_for_vehicle').replace(':vehicle', registration || '' ) }}
        <v-spacer/>

        <div
          class="x-image"
        >
          <v-img
            src="/img/icons/x-icon-image.svg"
            contain
            width="30"
            height="30"
            @click="closeDialog(false)"
          />
        </div>
      </v-card-title>
      <v-card-text
        class="pt-5 v-card-text"
        style="overflow-y: auto; flex-grow: 1;"
      >
        <v-row>
          <v-col>
            <v-menu
              v-model="openedDatePickers.date_start"
              transition="slide-y-transition"
              nudge-top="530"
              offset-y
              min-width="auto"
              :close-on-content-click="false"
            >
              <template
                #activator="{}"
              >
                <text-field
                  v-model="formData.date_start"
                  :field="{
                    key: 'date_start',
                    label: $t('fleet/vehicle_reservation.date_from'),
                    placeholder: 'DD.MM.YYYY. HH:mm',
                    appendIcon: 'mdi-calendar-month'
                  }"
                  :validation-errors="validationErrors"
                  @append-click="openDatePicker('date_start')"
                  @clear-validation-errors="$delete(validationErrors, 'date_start')"
                />
              </template>
              <datetime-picker
                v-if="openedDatePickers.date_start"
                v-model="formData.date_start"
                date-format="DD.MM.YYYY. HH:mm"
                :field="{
                   format: 'DD.MM.YYYY. HH:mm',
                   placeholder: 'DD.MM.YYYY. HH:mm',
                   minDate: currentDatetime('YYYY-MM-DD HH:mm')
                }"
                @close="closeDatePicker('date_start')"
                @dblclick:date="(instance) => onDateDblClick(instance)"
              />
            </v-menu>
          </v-col>
          <v-col>
            <v-menu
              v-model="openedDatePickers.date_end"
              transition="slide-y-transition"
              nudge-top="530"
              offset-y
              min-width="auto"
              :close-on-content-click="false"
            >
              <template
                #activator="{}"
              >
                <text-field
                  v-model="formData.date_end"
                  :field="{
                    key: 'date_end',
                    label: $t('fleet/vehicle_reservation.date_to'),
                    placeholder: 'DD.MM.YYYY. HH:mm',
                    appendIcon: 'mdi-calendar-month'
                  }"
                  :validation-errors="validationErrors"
                  @append-click="openDatePicker('date_end')"
                  @clear-validation-errors="$delete(validationErrors, 'date_end')"
                />
              </template>
              <datetime-picker
                v-if="openedDatePickers.date_end"
                v-model="formData.date_end"
                date-format="DD.MM.YYYY. HH:mm"
                :field="{
                   format: 'DD.MM.YYYY. HH:mm',
                   placeholder: 'DD.MM.YYYY. HH:mm',
                   minDate: currentDatetime('YYYY-MM-DD HH:mm'),
                   defaultTime: '23:59'
                }"
                @close="closeDatePicker('date_end')"
                @dblclick:date="(instance) => onDateDblClick(instance)"
              />
            </v-menu>
          </v-col>
        </v-row>

        <v-row
          v-if="isAdmin"
        >
          <v-col>
            <autocomplete-field
              v-model="formData.driver_id"
              ref="filterField"
              :options="{
                module: 'people',
                route: 'people/autocomplete'
              }"
              :dense="true"
              :field="{
                key: 'driver_id',
                label: $t('fleet/vehicle_reservation.choose_driver')
              }"
              :validation-errors="validationErrors"
            />
          </v-col>
        </v-row>

        <v-row
          v-if="responseMessage"
        >
          <v-col
            style="color: red;"
          >
            {{ responseMessage }}
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-row
          style="margin: 0;"
        >
          <v-col>
            <universal-button
              type="cancel"
              class="universal-buttons"
              @click="closeDialog(false)"
            />
          </v-col>
          <v-col>
            <universal-button
              type="save"
              :label="$t('fleet/vehicle_reservation.reserve')"
              class="universal-buttons"
              @click="makeReservation"
            />
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import TextField from '@/global/components/view-layouts/tabs-layout/components/fields/TextField.vue'
import AutocompleteField from '@/global/components/view-layouts/tabs-layout/components/fields/AutocompleteField.vue'
import UniversalButton from '@/global/components/buttons/UniversalButton.vue'
import DatetimePicker from '@/global/components/view-layouts/tabs-layout/components/fields/DatetimePicker.vue'
import { api } from '@/global/services/api'
import { formatIsoDateCustom, currentDatetime } from '@/global/services/helpers/dates'

const FORM_DATETIME_FORMAT = 'YYYY-MM-DD HH:mm'
const DISPLAY_DATETIME_FORMAT = 'DD.MM.YYYY. HH:mm'

export default {
  name: 'VehicleReservationDialog',

  components: {
    AutocompleteField,
    DatetimePicker,
    TextField,
    UniversalButton
  },

  props: {
    showDialog: {
      type: Boolean,
      default: false
    },
    isAdmin: {
      type: Boolean,
      default: false
    },
    vehicleId: {
      type: Number,
      default: null
    },
    registration: {
      type: String,
      default: ''
    }
  },

  emits: [
    'close'
  ],

  data () {
    return {
      openedDatePickers: [],
      canShowDialog: false,
      formData: {},
      responseMessage: null,
      validationErrors: {}
    }
  },

  watch: {
    showDialog: {
      immediate: true,
      handler (val) {
        this.canShowDialog = !!val
      }
    }
  },

  methods: {
    currentDatetime,

    async makeReservation () {
      try {
        if (!this.formData.date_start) {
          this.$set(this.validationErrors, 'date_start', this.$t('fleet/vehicle_reservation.start_date_mandatory'))
          return
        }

        if (!this.formData.date_end) {
          this.$set(this.validationErrors, 'date_end', this.$t('fleet/vehicle_reservation.end_date_mandatory'))
          return
        }

        const formData = new FormData()
        formData.append('date_start', formatIsoDateCustom(this.formData.date_start, DISPLAY_DATETIME_FORMAT, FORM_DATETIME_FORMAT))
        formData.append('date_end', formatIsoDateCustom(this.formData.date_end, DISPLAY_DATETIME_FORMAT, FORM_DATETIME_FORMAT))
        formData.append('vehicle_id', this.vehicleId ?? '')
        if (this.formData.driver_id) {
          formData.append('driver_id', this.formData.driver_id ?? '')
        }

        await api('fleet').post('vehicle-reservations/upsert', formData)
        this.closeDialog(true)
      }
      catch (exception) {
        console.error(exception)
        const { message } = exception?.response?.data || {}

        if (message) {
          this.responseMessage = message
        }
      }
    },

    closeDialog (refreshCalendar = false) {
      this.$emit('close', refreshCalendar)
    },

    openDatePicker (fieldKey) {
      if (!fieldKey) return

      this.$set(this.openedDatePickers, fieldKey, true)
    },

    closeDatePicker (fieldKey) {
      if (!fieldKey) return

      this.$set(this.openedDatePickers, fieldKey, false)
    },

    onDateDblClick (instance) {
      const { applyDate } = instance || {}

      if (applyDate && typeof applyDate === 'function') {
        applyDate()
      }
    }
  }
}
</script>

<style scoped lang="scss">
.v-card {
  max-width: 674px;
  border-radius: 16px;
}

.v-card-text {
  background-color: #FFFFFF;
  padding: 20px;
}

.v-card-title {
  font-family: 'Satoshi', sans-serif;
  font-weight: 500 !important;
  font-size: 24px !important;
  line-height: 32px !important;
  color: #111827 !important;
  border-bottom: 1px solid #E5E7EB;
}

.x-image:hover {
  cursor: pointer;
}

.universal-buttons {
  width: 100%;
  height: 40px !important;
}
</style>
